/**
 * Green Flag — MatchProfile
 *
 * Single-profile view used inside the Match screen's "profile" layout mode.
 * Renders the focused profile's hero photo, name/age, dating intention,
 * core values, and any additional media as a scrollable column. The Match
 * screen handles the sticky chrome (header, search, tabs) above this.
 */

import { Box, Stack, chakra } from "@chakra-ui/react";
import {
  getDatingIntentionLabel,
  getDatingIntentionValueCategoryName,
  type UserProfile,
} from "@greenflagdate/shared";
import { motion } from "framer-motion";
import { useLayoutEffect, useRef, useState } from "react";
import {
  MediaBlobImage,
  profileImagePaths,
} from "../../../lib/profileMedia";
import { Icon } from "../../../components/Icon/Icon";
import { FriendsOnlyNote } from "../../../components/FriendsOnlyNote/FriendsOnlyNote";
import { FloatingActionButton } from "../../../components/FloatingActionButton/FloatingActionButton";

const MChakraDiv = motion.create(chakra.div);

export type MatchProfileDecision = "match" | "pass";

export interface MatchProfileProps {
  profile: UserProfile;
  /**
   * Whether this pairing is a dating or a friendship context — the same value
   * the grid tiles use to pick their label. Drives the decision buttons, so a
   * friendship is never recorded by tapping a heart. Defaults to "dating";
   * the /spark deck never needs to pass it, since Friends Only users are
   * excluded from it server-side.
   */
  category?: "dating" | "friend";
  /**
   * When provided, renders a pair of circular decision buttons overlapping the
   * bottom edge of the hero photo. The callback fires with "match" (right,
   * brand-tinted) or "pass" (left, danger-tinted).
   */
  onDecision?: (decision: MatchProfileDecision) => void;
  /**
   * Extra space below the decision buttons. Stacks on top of the safe-area
   * inset and the default 24px gap. Use this to clear a bottom nav bar.
   */
  decisionButtonsBottomOffset?: string;
  /**
   * The next profile in the queue. When set, its full card content is
   * rendered as a static layer behind the animating card so swiping away
   * reveals the next person in place — no flash of empty space and no
   * late-arriving info once the new card mounts on top.
   */
  behindProfile?: UserProfile | null;
}

/**
 * Everything inside a profile card except the decision buttons — hero photo,
 * name/age chip, dating intention, values, and extra photos. Extracted so the
 * swipe-behind layer can render the full next profile underneath (not just
 * the hero), avoiding a flash when the top card finishes its exit animation
 * and the new card mounts with the rest of its content.
 */
function ProfileCardContent({
  profile,
  paddingBottom,
}: {
  profile: UserProfile;
  paddingBottom?: string;
}) {
  const images = profileImagePaths(profile);
  const hero = images[0];

  return (
    <>
      <Box px="5">
        <Box
          w="full"
          aspectRatio="1 / 1"
          overflow="hidden"
          borderRadius="16px"
          bg="border.subtle"
        >
          <MediaBlobImage
            path={hero}
            alt={profile.firstName ?? "Profile"}
            instant
            css={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            // Only claim "no photos yet" when the profile genuinely has none.
            // When a hero image exists but its blob is still loading or fails,
            // fall back to the plain grey slot instead of the misleading
            // placeholder — the user does have photos (and they render below).
            fallback={
              images.length === 0 ? (
                <Stack align="center" gap="3" px="6" textAlign="center">
                  <Icon name="user-solid" boxSize="80px" />
                  <chakra.p textStyle="body" m="0" color="fg.brand">
                    {profile.firstName
                      ? `${profile.firstName} hasn't added photos yet`
                      : "No photos added yet"}
                  </chakra.p>
                </Stack>
              ) : undefined
            }
          />

        </Box>
      </Box>
      <Box px="5" pt="5" pb={paddingBottom}>
        <Box display="flex" alignItems="center" gap="3" flexWrap="wrap">
          <chakra.h2
            textStyle="bodyLg"
            fontSize="1.5rem"
            fontWeight="bold"
            m="0"
          >
            {profile.firstName}
            {profile.age != null && (
              <chakra.span
                textStyle="bodyLg"
                fontSize="1.5rem"
                fontWeight="bold"
                color="fg.default"
              >
                {`, ${profile.age}`}
              </chakra.span>
            )}
          </chakra.h2>
          {profile.lastActiveWithin && (
            <Box
              display="inline-flex"
              alignItems="center"
              gap="1"
              whiteSpace="nowrap"
              fontSize="xs"
              fontWeight="semibold"
              color="fg.brand"
              bg="bg.brandSoft"
              px="2"
              py="1"
              borderRadius="full"
            >
              <Box w="6px" h="6px" borderRadius="full" bg="fg.brand" />
              Active within the last {profile.lastActiveWithin}
            </Box>
          )}
        </Box>

        {profile.friendsOnly && (
          <Box mt="5">
            <chakra.h3 textStyle="bodyLg" fontWeight="medium" m="0" mb="2">
              Looking For
            </chakra.h3>
            <FriendsOnlyNote />
          </Box>
        )}

        {profile.datingIntention && (
          <Box mt="5">
            <chakra.h3 textStyle="bodyLg" fontWeight="medium" m="0" mb="2">
              Dating Intention
            </chakra.h3>
            <Box
              textStyle="body"
              borderWidth="1px"
              borderColor="border.subtle"
              borderRadius="md"
              px="4"
              py="3"
              color="fg.default"
            >
              {getDatingIntentionLabel(profile.datingIntention)}
            </Box>
          </Box>
        )}

        {profile.datingIntention &&
          profile.values.filter(Boolean).length > 0 && (
            <Box mt="5">
              <chakra.h3 textStyle="bodyLg" fontWeight="medium" m="0" mb="2">
                {getDatingIntentionValueCategoryName(profile.datingIntention)}
              </chakra.h3>
              <Stack gap="2">
                {profile.values.filter(Boolean).map((v, i) => (
                  <Box
                    key={`${profile.id}-val-${i}`}
                    textStyle="body"
                    borderWidth="1px"
                    borderColor="border.subtle"
                    borderRadius="md"
                    px="4"
                    py="3"
                  >
                    {v}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

        {images.slice(1).map((path, i) => (
          <Box
            key={`${profile.id}-photo-${i}`}
            mt="5"
            w="full"
            aspectRatio="1 / 1"
            overflow="hidden"
            borderRadius="16px"
            bg="border.subtle"
          >
            <MediaBlobImage
              path={path}
              alt=""
              css={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        ))}
      </Box>
    </>
  );
}

export function MatchProfile({
  profile,
  category = "dating",
  onDecision,
  decisionButtonsBottomOffset,
  behindProfile,
}: MatchProfileProps) {
  const isFriend = category === "friend";
  // Reserve enough room for the fixed decision buttons (70px tall, sitting
  // 24px above the safe-area inset) plus a 16px breathing gap above them,
  // so scrolling to the bottom never lets the buttons overlap the name,
  // photos, or other content.
  const contentPaddingBottom = onDecision
    ? `calc(env(safe-area-inset-bottom) + 24px + 70px + 16px${decisionButtonsBottomOffset ? ` + ${decisionButtonsBottomOffset}` : ""})`
    : undefined;

  const [exitDirection, setExitDirection] = useState<"left" | "right" | null>(
    null
  );

  // Clear the exit direction synchronously the first time we see a new
  // profile id. Otherwise the swipe state leaks into the next card — the
  // freshly-mounted MChakraBox would read `animate: { x: 480 }` and slide
  // itself right back off screen.
  const lastProfileIdRef = useRef(profile.id);
  if (lastProfileIdRef.current !== profile.id) {
    lastProfileIdRef.current = profile.id;
    if (exitDirection !== null) setExitDirection(null);
  }

  // Reset the scroll position to the top when we advance to a new profile.
  // The scrollable container is an ancestor (ModalPage's overflowY:auto Box),
  // not this component, and it persists across profile swaps — so without
  // this the next card appears already scrolled down to wherever the previous
  // one was left. useLayoutEffect runs before paint, so there's no flash of
  // the scrolled-down state.
  const rootRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    let el = rootRef.current?.parentElement;
    while (el) {
      const { overflowY } = getComputedStyle(el);
      if (overflowY === "auto" || overflowY === "scroll") {
        el.scrollTop = 0;
        return;
      }
      el = el.parentElement;
    }
  }, [profile.id]);

  const triggerDecision = (d: MatchProfileDecision) => {
    if (exitDirection || !onDecision) return;
    setExitDirection(d === "match" ? "right" : "left");
  };

  return (
    <Box
      ref={rootRef}
      position="relative"
      w="full"
      maxW="30rem"
      mx="auto"
      minH="100%"
      display="flex"
      flexDirection="column"
    >
      {/* Behind-card preview — the next profile rendered in place behind
          the top card. Uses the same wrapper styling as the top card so
          positions line up pixel-for-pixel and the new top card's mount
          doesn't cause a visible shift. Non-interactive and ignored by AT.

          Pinned top:0/bottom:0 so its height is exactly the parent's (which
          the top card drives), and `overflow: hidden` clips any extra height.
          Without the clip, a taller next profile (e.g. 3 photos behind a
          1-photo top card) spills past the bottom of the opaque top card and
          its extra photos peek out below. The behind layer only needs to show
          the slice the top card vacates as it swipes off, so clipping to the
          top card's height loses nothing. */}
      {behindProfile && (
        <chakra.div
          key={`behind-${behindProfile.id}`}
          css={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            width: "100%",
            overflow: "hidden",
            paddingTop: "var(--chakra-spacing-4)",
            display: "flex",
            flexDirection: "column",
          }}
          pointerEvents="none"
          aria-hidden="true"
        >
          <ProfileCardContent
            profile={behindProfile}
            paddingBottom={contentPaddingBottom}
          />
        </chakra.div>
      )}

      {/* Top card wrapper. Matches the behind layer's layout so the two
          line up, and holds the decision buttons so they stay put during
          swipe (only the animating MChakraDiv translates off). */}
      <Box
        position="relative"
        zIndex={1}
        w="full"
        minH="100%"
        pt="4"
        display="flex"
        flexDirection="column"
      >
        <MChakraDiv
          // Re-key on profile id so each new card fresh-mounts at rest.
          // The inner MChakraDiv is what actually swipes off — the outer
          // wrapper (and the decision buttons below) stay put.
          key={profile.id}
          css={{
            display: "flex",
            flexDirection: "column",
            flex: "1",
            // Opaque so the behind-card layer is only visible once the
            // content translates off-screen.
            background: "var(--chakra-colors-bg-canvas)",
          }}
          initial={{ x: 0, rotate: 0, opacity: 1 }}
          animate={
            exitDirection
              ? {
                  x: exitDirection === "right" ? 480 : -480,
                  rotate: exitDirection === "right" ? 22 : -22,
                  opacity: 0,
                }
              : { x: 0, rotate: 0, opacity: 1 }
          }
          transition={{ type: "spring", stiffness: 260, damping: 28 }}
          onAnimationComplete={() => {
            if (!exitDirection) return;
            onDecision?.(exitDirection === "right" ? "match" : "pass");
          }}
        >
          <ProfileCardContent
            profile={profile}
            paddingBottom={contentPaddingBottom}
          />
        </MChakraDiv>

        {onDecision && (
          <Box
            // Always fixed so the buttons are viewport-anchored — they stay
            // put when the profile underneath changes height across swaps,
            // and never ride along with the animating card.
            position="fixed"
            bottom={`calc(env(safe-area-inset-bottom) + 24px${decisionButtonsBottomOffset ? ` + ${decisionButtonsBottomOffset}` : ""})`}
            left="0"
            right="0"
            maxW="30rem"
            mx="auto"
            display="flex"
            justifyContent="space-between"
            px="6"
            zIndex="50"
            pointerEvents="none"
          >
            <FloatingActionButton
              aria-label={isFriend ? "Not a friend" : "Don't message"}
              onClick={() => triggerDecision("pass")}
              bg="bg.canvas"
              color="fg.danger"
              pointerEvents="auto"
            >
              {/* A broken heart reads as romantic rejection, which is the
                  wrong frame for declining a friendship. */}
              <Icon name={isFriend ? "x" : "heart-broken"} boxSize="28px" />
            </FloatingActionButton>
            <FloatingActionButton
              aria-label={isFriend ? "Add friend" : "Message"}
              onClick={() => triggerDecision("match")}
              bg="bg.canvas"
              color={isFriend ? "fg.friends" : "fg.brand"}
              pointerEvents="auto"
            >
              <Icon name={isFriend ? "smile" : "heart"} boxSize="28px" />
            </FloatingActionButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}

